import React, { useState, useRef, useEffect } from 'react';

interface Option {
  value: string;
  label: string;
}

interface MultiSelectFilterProps {
  options: Option[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
}

export default function MultiSelectFilter({ options, selected, onChange, placeholder = 'Select...' }: MultiSelectFilterProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const filtered = options.filter(o =>
    (o.label ?? '').toLowerCase().includes(search.toLowerCase())
  );

  function toggle(value: string) {
    if (selected.includes(value)) {
      onChange(selected.filter(v => v !== value));
    } else {
      onChange([...selected, value]);
    }
  }

  const label = selected.length === 0
    ? placeholder
    : `${selected.length} selected`;

  return (
    <div className="multi-select" ref={wrapRef}>
      <button
        type="button"
        className="multi-select__trigger"
        onClick={() => setOpen(o => !o)}
      >
        <span>{label}</span>
        <span className="multi-select__chevron">{open ? '\u25B2' : '\u25BC'}</span>
      </button>

      {open && (
        <div className="multi-select__dropdown">
          <input
            className="multi-select__search"
            type="text"
            placeholder="Search..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            autoFocus
          />
          <div className="multi-select__actions">
            <button type="button" onClick={() => onChange(options.map(o => o.value))}>Select all</button>
            <button type="button" onClick={() => onChange([])}>Clear</button>
          </div>
          <div className="multi-select__list">
            {filtered.map(o => (
              <label key={o.value} className="multi-select__option">
                <input
                  type="checkbox"
                  checked={selected.includes(o.value)}
                  onChange={() => toggle(o.value)}
                />
                <span>{o.label}</span>
              </label>
            ))}
            {filtered.length === 0 && (
              <p className="multi-select__empty">No matches</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
